import { Injectable, OnDestroy } from '@angular/core';
import Graphic from '@arcgis/core/Graphic';
import { defer, from, map } from 'rxjs';
import {
  MapNoteModel,
  MapNoteModelLike,
  MapNoteType,
} from '../../models/map-note.model';
import { MapSymbolService } from '../../services/map-symbol.service';
import { ObjectUtils } from '../../utils/object.utils';

@Injectable()
export class NotesService implements OnDestroy {
  private graphicsLayer: __esri.GraphicsLayer;

  private _initialized = false;
  public get initialized() {
    return this._initialized;
  }

  private _destroyed = false;
  public get destroyed() {
    return this._destroyed;
  }

  private _noteGraphicMap = new Map<
    string,
    { note: MapNoteModelLike; graphic: __esri.Graphic }
  >();

  constructor(private mapSymbolService: MapSymbolService) {}

  ngOnDestroy(): void {
    this.destroy();
  }

  setup(layer: __esri.GraphicsLayer) {
    if (this.initialized || this.destroyed) {
      return;
    }

    this._initialized = true;
    this.graphicsLayer = layer;
  }

  destroy() {
    if (!this.initialized || this.destroyed) {
      return;
    }

    this.graphicsLayer = null;
    this._destroyed = true;
  }

  getNotes() {
    return Array.from(this._noteGraphicMap.values(), (x) =>
      ObjectUtils.clone(x.note)
    );
  }

  setNotes(notes: MapNoteModelLike[]) {
    if (!this.initialized || this.destroyed) {
      return;
    }

    this.removeAllNotes();

    notes?.forEach((note) => {
      this.addNote(note);
    });
  }

  addNoteAndGraphic(note: MapNoteModelLike, graphic: __esri.Graphic) {
    if (!this.initialized || this.destroyed) {
      return;
    }

    if (!note || !graphic) {
      return;
    }

    note = new MapNoteModel(note);
    const key = note.key;
    if (this._noteGraphicMap.has(key)) {
      return;
    }

    graphic.attributes = { key };
    if (!this.graphicsLayer.graphics.includes(graphic)) {
      this.graphicsLayer.add(graphic);
    }
    this._noteGraphicMap.set(key, { note, graphic });
  }

  addNote(note: MapNoteModelLike) {
    if (!this.initialized || this.destroyed) {
      return;
    }

    if (!note) {
      return;
    }

    note = new MapNoteModel(note);
    const key = note.key;
    if (!this._noteGraphicMap.has(key)) {
      const symbol = this.handleSymbol(note.type, note.symbolParams);
      const graphic = new Graphic({
        geometry: note.geometry,
        symbol,
        attributes: {
          key,
        },
      });

      this.graphicsLayer.add(graphic);
      this._noteGraphicMap.set(key, { note, graphic });
    }
  }

  updateNote(note: MapNoteModelLike) {
    if (!this.initialized || this.destroyed) {
      return;
    }

    if (!note) {
      return;
    }

    const key = note?.key;
    if (this._noteGraphicMap.has(key)) {
      const holder = this._noteGraphicMap.get(key);
      note = ObjectUtils.clone(note);

      const symbol = this.handleSymbol(note.type, note.symbolParams);
      const graphic = holder.graphic;
      if (graphic) {
        graphic.symbol = symbol;
        graphic.geometry = note.geometry;
      }

      holder.note = note;
    }
  }

  addOrUpdateNote(note: MapNoteModelLike) {
    const key = note?.key;
    if (this._noteGraphicMap.has(key)) {
      this.updateNote(note);
    } else {
      this.addNote(note);
    }
  }

  removeNote(note: MapNoteModelLike) {
    if (!this.initialized || this.destroyed) {
      return;
    }

    const key = note?.key;
    if (this._noteGraphicMap.has(key)) {
      const holder = this._noteGraphicMap.get(key);
      this.graphicsLayer.remove(holder.graphic);
      this._noteGraphicMap.delete(key);
    }
  }

  removeAllNotes() {
    if (!this.initialized || this.destroyed) {
      return;
    }

    const graphics = Array.from(
      this._noteGraphicMap.values(),
      (x) => x.graphic
    );

    this._noteGraphicMap.clear();
    this.graphicsLayer.removeMany(graphics);
  }

  findNoteByKey(key: string) {
    return ObjectUtils.clone(this._noteGraphicMap.get(key)?.note);
  }

  hitTest(mapView: __esri.MapView, screenPoint: __esri.ScreenPoint) {
    return defer(() =>
      from(mapView.hitTest(screenPoint, { include: this.graphicsLayer }))
    ).pipe(
      map((res) => {
        if (!res.results.length) {
          return null;
        }

        const graphic = (res.results[0] as __esri.GraphicHit).graphic;
        const key = graphic.attributes?.key;
        const note = ObjectUtils.clone(this._noteGraphicMap.get(key)?.note);
        if (!note) {
          return null;
        }

        return { note, graphic };
      })
    );
  }

  private handleSymbol(type: MapNoteType, symbolParams: any) {
    let symbol: __esri.Symbol;
    switch (type) {
      case MapNoteType.Point:
        symbol = this.mapSymbolService.createPointSymbol(symbolParams);
        break;
      case MapNoteType.Line:
      case MapNoteType.Polyline:
      case MapNoteType.FreePolyline:
        symbol = this.mapSymbolService.createLineSymbol(symbolParams);
        break;
      case MapNoteType.Polygon:
      case MapNoteType.FreePolygon:
      case MapNoteType.Circle:
      case MapNoteType.Rectangle:
      case MapNoteType.Arrow:
      case MapNoteType.Triangle:
      case MapNoteType.Ellipse:
        symbol = this.mapSymbolService.createPolygonSymbol(symbolParams);
        break;
      case MapNoteType.Text:
        symbol = this.mapSymbolService.createTextSymbol(symbolParams);
        break;
    }

    return symbol;
  }
}
